import PlaceholderPicture from '@assets/images/placeholder.png';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useTruncate } from '@hooks/useTruncate';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Button, Popover, Tooltip, CircularProgress } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useUsersStore } from 'services/users';
import { chatService } from '../../../services/chat/chatService';

const useIsMobile = () => {
	const [isMobile, setIsMobile] = React.useState(false);
	const truncate = useTruncate();

	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.matchMedia('(max-width: 768px)').matches);
		};
		checkIsMobile();
		window.addEventListener('resize', checkIsMobile);
		return () => window.removeEventListener('resize', checkIsMobile);
	}, []);

	return isMobile;
};

export function MonitoringPhaseHeader({ data }: any) {
	const { t } = useTranslation();
	const { user } = useUsersStore();
	const { navigateWithLanguage } = useLanguageNavigate();
	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);
	const isMobile = useIsMobile();
	const { truncate } = useTruncate();
	const [chatLoading, setChatLoading] = useState(false);

	const isFarmerPath = useLocation().pathname.includes('farmer');

	const handlePopoverOpen = (event: React.MouseEvent<Element>) => {
		if (isMobile) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleMouseEnter = (event: React.MouseEvent<Element>) => {
		if (!isMobile) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleMouseLeave = () => {
		if (!isMobile) {
			setAnchorEl(null);
		}
	};

	const handlePopoverClose = () => {
		if (isMobile) {
			setAnchorEl(null);
		}
	};

	const open = Boolean(anchorEl);

	const specialistProfilePicture = data?.specialist?.profile_image
		? data?.specialist?.profile_image
		: PlaceholderPicture;

	const isCompleted = moment(data?.phases_management?.active[0]?.phases_closed).isBefore(moment());
	const daysLeft = moment(data?.phases_management?.active[0]?.phases_closed).diff(moment(), 'days');

	const userRole = user?.role?.toLowerCase();
	const userData = !isFarmerPath ? data?.farmer : data?.specialist;

	const truncatedFullName = truncate(`${userData?.first_name} ${userData?.last_name}`, 20);

	const handleChatClick = useCallback(async () => {
		if (!data?.id) return;
		
		setChatLoading(true);
		try {
			const channel = await chatService.GetChannelByMonitoringId(data.id);
			if (channel?.id) {
				navigateWithLanguage(`/${userRole}/chats?channelId=${channel.id}`);
			} else {
				// If no channel found, navigate to chats without a specific channel
				navigateWithLanguage(`/${userRole}/chats`);
				console.error('No channel ID found for monitoring offer');
			}
		} catch (error) {
			console.error('Error fetching channel:', error);
			// Still navigate to chats even if there's an error
			navigateWithLanguage(`/${userRole}/chats`);
		} finally {
			setChatLoading(false);
		}
	}, [data?.id, navigateWithLanguage, userRole]);

	// Mobile view
	if (isMobile) {
		return (
			<div className="bg-[#F8F9FA] md:bg-white border-y border-light w-full flex flex-col py-6 px-4">
				<h1 className="text-xl font-bold text-center mb-4">
					{data?.phase_name || 'Phase: Late Dormancy'}
				</h1>

				<div className="flex items-center gap-4 mb-4">
					<img
						src={data?.crop?.image_url}
						alt={data?.crop?.name}
						className="w-[75px] h-[75px] rounded-xl shadow-lg"
					/>
					<div className="flex flex-col">
						<span className="font-bold text-base text-dark font-roboto">
							{data?.crop?.name || 'Almond'}
						</span>
						<div className="font-bold text-dark text-base gap-2 flex items-center cursor-pointer mt-1">
							<BookOutlinedIcon className="text-disabled" />
							<span className="underline text-disabled">{t('fieldbook')}</span>
						</div>
					</div>
				</div>

				<div className="flex items-center gap-4 mb-4">
					<img
						src={specialistProfilePicture}
						alt={truncatedFullName}
						className="w-[75px] h-[75px] rounded-xl shadow-lg"
					/>
					<div className="flex flex-col">
						<Tooltip title={truncatedFullName || ''} arrow>
							<span className="font-bold text-base text-dark font-roboto">{truncatedFullName}</span>
						</Tooltip>
						<div className="font-bold text-base gap-2 flex items-center cursor-pointer mt-1">
							<Button
								variant="text"
								color="primary"
								startIcon={chatLoading ? <CircularProgress size={16} /> : <QuestionAnswerIcon />}
								sx={{
									textDecoration: 'underline',
									textTransform: 'none',
									padding: 0,
									minWidth: 'auto',
								}}
								onClick={handleChatClick}
								disabled={chatLoading}
							>
								{t('chat')}
							</Button>
						</div>
					</div>
				</div>

				<div className="flex flex-col items-center mt-2">
					<div className="flex items-center gap-2">
						<span className="font-bold text-base text-dark font-roboto">{`${moment(data?.phases_management?.active[0]?.phases_open).format('DD.MM.YYYY')} - ${moment(data?.phases_management?.active[0]?.phases_closed).format('DD.MM.YYYY')}`}</span>
						<InfoOutlinedIcon className="text-blue cursor-pointer" onClick={handlePopoverOpen} />
						<Popover
							open={open}
							anchorEl={anchorEl}
							onClose={handlePopoverClose}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'center',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'center',
							}}
							sx={{
								pointerEvents: 'auto',
							}}
						>
							<div className="p-3 flex items-center gap-2">
								<InfoOutlinedIcon className="text-blue cursor-pointer" />
								<p className="text-blue text-base">
									{t('ensureAllMonitoringDataIsCompletedWithinTheSpecifiedDates')}
								</p>
							</div>
						</Popover>
					</div>
					{/*<span className="text-base text-light-grey text-[11px] font-normal mt-1">*/}
					{/*	{isCompleted ? 'Completed' : t('daysLeft', { days: daysLeft })}*/}
					{/*</span>*/}
				</div>
			</div>
		);
	}

	// Desktop view
	return (
		<div className="bg-white border-y border-light w-full flex justify-between items-center py-3">
			<div className="flex justify-center gap-5">
				<img
					src={data?.crop?.image_url}
					alt={data?.crop?.name}
					className="w-[75px] h-[75px] rounded-xl shadow-lg"
				/>
				<div className="flex flex-col justify-center gap-1">
					<span className="font-bold text-base text-dark font-roboto pt-3">{data?.crop?.name}</span>
					<div className="font-bold text-dark text-base gap-3 flex items-center cursor-pointer">
						<BookOutlinedIcon className="text-disabled" />
						<span className="underline text-disabled">{t('fieldbook')}</span>
					</div>
				</div>
			</div>
			<div className="flex justify-center gap-5 border-x border-light px-[44px]">
				<img
					src={specialistProfilePicture}
					alt={`${userData?.first_name} ${userData?.last_name}`}
					className="w-[75px] h-[75px] rounded-xl shadow-lg"
				/>
				<div className="flex flex-col justify-center gap-1">
					<Tooltip title={truncatedFullName || ''} arrow>
						<span className="font-bold text-base text-dark font-roboto">{truncatedFullName}</span>
					</Tooltip>
					<div className="font-bold text-base gap-3 flex items-center cursor-pointer">
						<Button
							variant="text"
							color="primary"
							startIcon={chatLoading ? <CircularProgress size={16} /> : <QuestionAnswerIcon />}
							sx={{
								textDecoration: 'underline',
								textTransform: 'none',
								padding: 0,
								minWidth: 'auto',
							}}
							onClick={handleChatClick}
							disabled={chatLoading}
						>
							{t('chat')}
						</Button>
					</div>
				</div>
			</div>
			{/*<div className="flex flex-col justify-start items-start gap-1">*/}
			{/*	<div className="flex items-center gap-2">*/}
			{/*		<span className="font-bold text-base text-dark font-roboto">{`${moment(data?.phases_management?.active[0]?.phases_open).format('DD.MM.YYYY')} - ${moment(data?.phases_management?.active[0]?.phases_closed).format('DD.MM.YYYY')}`}</span>*/}
			{/*		<InfoOutlinedIcon*/}
			{/*			className="text-blue cursor-pointer"*/}
			{/*			onClick={handlePopoverOpen}*/}
			{/*			onMouseEnter={handleMouseEnter}*/}
			{/*			onMouseLeave={handleMouseLeave}*/}
			{/*		/>*/}
			{/*		<Popover*/}
			{/*			open={open}*/}
			{/*			anchorEl={anchorEl}*/}
			{/*			onClose={handlePopoverClose}*/}
			{/*			anchorOrigin={{*/}
			{/*				vertical: 'bottom',*/}
			{/*				horizontal: 'center',*/}
			{/*			}}*/}
			{/*			transformOrigin={{*/}
			{/*				vertical: 'top',*/}
			{/*				horizontal: 'center',*/}
			{/*			}}*/}
			{/*			sx={{*/}
			{/*				pointerEvents: isMobile ? 'auto' : 'none',*/}
			{/*			}}*/}
			{/*		>*/}
			{/*			<div*/}
			{/*				className="p-3 flex items-center gap-2"*/}
			{/*				onMouseEnter={handleMouseEnter}*/}
			{/*				onMouseLeave={handleMouseLeave}*/}
			{/*			>*/}
			{/*				<InfoOutlinedIcon className="text-blue cursor-pointer" />*/}
			{/*				<p className="text-blue text-base">*/}
			{/*					{t('ensureAllMonitoringDataIsCompletedWithinTheSpecifiedDates')}*/}
			{/*				</p>*/}
			{/*			</div>*/}
			{/*		</Popover>*/}
			{/*	</div>*/}
			{/*	<span className="text-base text-light-grey text-[11px] font-normal">*/}
			{/*		{isCompleted ? t('completed') : t('daysLeft', { days: daysLeft })}*/}
			{/*	</span>*/}
			{/*</div>*/}
		</div>
	);
}
